import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTheme, alpha } from '@mui/material'
import AppBoxComponent from 'components/mui/AppBoxComponent'
import AppTypographyComponent from 'components/mui/AppTypographyComponent'
import AppButtonComponent from 'components/mui/AppButtonComponent'
import AppCardComponent from 'components/mui/AppCardComponent'
import { Grid, Chip, LinearProgress } from '@mui/material'
import { formatCurrencyDetailed, formatPercentage, formatCurrency } from 'utils/currency.utils'
import { typography } from 'theme'
import DonutChartContainer from 'containers/DonutChartContainer'
import CommonAreaChart from 'components/chart/CommonAreaChart'
import { useGetPortfolioPerformanceQuery, useGetAssetAllocationQuery } from 'store/api/portfolio.apislice'
import ComingSoonCard from '../../components/common/ComingSoonCard'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useNavigate } from 'react-router-dom'
import ROUTES from 'router/routes.constants'


const PortfolioSummaryView = ({
  portfolioData = {},
  performanceComingSoon = false,
  reverseOrder = false,
  holdingsCardsResponse,
}) => {
  // Component scope constants
  const TIME_RANGES = ["1M", "6M", "1Y", "3Y", "5Y", "ALL"];
  const FILTER_OPTIONS = ["Asset", "Category", "Market Cap", "AMC"];

  // Colors for gradients
  const GRADIENTS = [
    "linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%)", // Violet
    "linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)", // Blue
    "linear-gradient(90deg, #A855F7 0%, #D8B4FE 100%)", // Purple
    "linear-gradient(90deg, #EC4899 0%, #F472B6 100%)", // Pink
    "linear-gradient(90deg, #10B981 0%, #34D399 100%)", // Emerald
    "linear-gradient(90deg, #F59E0B 0%, #FBBF24 100%)", // Amber
  ];

  const SOLID_COLORS = [
    "#8B5CF6", // Violet
    "#3B82F6", // Blue
    "#A855F7", // Purple
    "#EC4899", // Pink
    "#10B981", // Emerald
    "#F59E0B", // Amber
  ];

  const theme = useTheme();
  const navigate = useNavigate();
  // States
  const [selectedTimeRange, setSelectedTimeRange] = useState("1M");
  const [selectedFilter, setSelectedFilter] = useState("Asset");

  // const { data: performanceData, isLoading } = useGetPortfolioPerformanceQuery(selectedTimeRange)
  // const { data: assetAllocationResponse, isLoading: isAssetLoading } = useGetAssetAllocationQuery(selectedFilter === 'Market Cap' ? 'MARKETCAP' : selectedFilter);

  const { data: apiPerformanceData, isLoading } =
    useGetPortfolioPerformanceQuery(selectedTimeRange, {
      skip: !!portfolioData?.performanceData,
    });

  const { data: apiAssetAllocationResponse, isLoading: isAssetLoading } =
    useGetAssetAllocationQuery(
      selectedFilter === "Market Cap" ? "MARKETCAP" : selectedFilter,
      {
        skip: !!portfolioData?.assetAllocationData,
      },
    );

  // Auto Switch API or Mock

  const performanceData = portfolioData?.performanceData || apiPerformanceData;

  const assetAllocationResponse =
    portfolioData?.assetAllocationData || apiAssetAllocationResponse;

  const MOCK_HOLDINGS = useMemo(() => [
    {
      name: "Mutual Funds",
      currentValue: 8040000,
      returns: 142000,
      countValue: "4 funds",
      oneDayReturns: 3.1
    },
    {
      name: "Bonds",
      currentValue: 1040000,
      returns: null,
      countValue: "3 securities",
      oneDayReturns: null
    }
  ], []);

  const formatToLacOrCr = (amount) => {
    if (amount === null || amount === undefined) return '₹0';
    const formatted = formatCurrency(amount);
    if (formatted.endsWith('L')) {
      return formatted.slice(0, -1) + ' Lac';
    }
    if (formatted.endsWith('Cr')) {
      return formatted.slice(0, -2) + ' Cr';
    }
    return formatted;
  };

  const renderReturns = (returnsVal, percentageVal) => {
    if (returnsVal === null || returnsVal === undefined) return null;

    const isPositive = returnsVal >= 0;
    const arrow = isPositive ? '▲' : '▼';
    const sign = isPositive ? '+' : '-';
    const formattedReturns = formatCurrency(Math.abs(returnsVal));

    let pctPart = '';
    if (percentageVal !== null && percentageVal !== undefined) {
      const numPct = typeof percentageVal === 'string' ? parseFloat(percentageVal) : percentageVal;
      if (!isNaN(numPct)) {
        pctPart = ` (${isPositive ? '+' : '-'}${Math.abs(numPct).toFixed(2)}%)`;
      }
    }

    return `${arrow} ${sign}${formattedReturns}${pctPart}`;
  };


  console.log("holddd", holdingsCardsResponse)
  const holdingsCardsData = useMemo(() => {
    return holdingsCardsResponse?.result || holdingsCardsResponse?.data || MOCK_HOLDINGS;
  }, [holdingsCardsResponse, MOCK_HOLDINGS]);

  const mutualFundsData = useMemo(() => {
    return holdingsCardsData.find(item => item.name === 'Mutual Funds') || MOCK_HOLDINGS[0];
  }, [holdingsCardsData, MOCK_HOLDINGS]);

  const bondsData = useMemo(() => {
    return holdingsCardsData.find(item => item.name === 'Bonds') || MOCK_HOLDINGS[1];
  }, [holdingsCardsData, MOCK_HOLDINGS]);

  // Debugging logs
  console.log("Asset Allocation Response:", assetAllocationResponse);

  // Transform API data for chart
  const chartData = useMemo(() => {
    const dataArray =
      performanceData?.result?.data || performanceData?.data || [];

    if (!Array.isArray(dataArray)) return [];

    return dataArray.map((item) => ({
      date: item.date,
      value: Number(item.graphValue) || 0,
    }));
  }, [performanceData]);

  // Transform Asset Allocation Data
  const mappedAssetCategories = useMemo(() => {
    const result = assetAllocationResponse?.result || {};
    let rawData = [];

    // Select specific data array based on the selected filter (and potentially the API response structure)
    if (selectedFilter === "Asset" && result.byHoldingType) {
      rawData = result.byHoldingType.map((item) => ({
        name: item.type,
        value: item.percentage,
      }));
    } else if (selectedFilter === "Category" && result.byCategory) {
      rawData = result.byCategory.map((item) => ({
        name: item.category,
        value: item.value,
      }));
    } else if (selectedFilter === "Market Cap" && result.byMarketCap) {
      rawData = result.byMarketCap.map((item) => ({
        name: item.cap,
        value: item.value,
      }));
    } else if (selectedFilter === "AMC" && result.byAmc) {
      rawData = result.byAmc.map((item) => ({
        name: item.amcName,
        value: item.value,
      }));
    } else {
      // Fallback or if the API returns a generic 'data' array (previous assumption)
      rawData = result.data || [];
    }

    console.log("Processed Raw Asset Data:", rawData);

    return rawData
      .map((item, index) => {
        // Parse value string "99.1%" -> 99.1
        const numericValue =
          typeof item.value === "string"
            ? parseFloat(item.value.replace("%", ""))
            : parseFloat(item.value) || 0;

        return {
          name: item.name || item.type || "Unknown",
          percentage: numericValue,
          gradient: GRADIENTS[index % GRADIENTS.length],
          solid: SOLID_COLORS[index % SOLID_COLORS.length],
        };
      })
      .filter((item) => item.percentage > 0); // Optional: filter out 0% items if desired
  }, [assetAllocationResponse, selectedFilter]);

  // Other functions and logic
  const handleTimeRangeChange = (range) => {
    setSelectedTimeRange(range);
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
  };

  const performanceSection = (
    <AppCardComponent
      sx={{
        p: theme.spacing(1),
        mb: theme.spacing(3),
        backgroundColor:
          theme.palette.custom?.background?.card || theme.palette.grey[900],
        border: "none",
        borderRadius: theme.spacing(1.5),
        position: "relative",
        overflow: "hidden",
      }}
    >
      <AppBoxComponent
        sx={{
          filter: performanceComingSoon ? "blur(5px)" : "none",
          pointerEvents: performanceComingSoon ? "none" : "auto",
        }}
      >
        <AppTypographyComponent
          sx={{
            color: theme.palette.custom?.text?.heading,
            fontSize: {
              xs: typography.fontSize.body1,
              md: typography.fontSize.h4,
            },
            fontWeight: typography.fontWeight.semibold,
            mb: { xs: theme.spacing(2), md: theme.spacing(3) },
          }}
        >
          Portfolio Performance
        </AppTypographyComponent>

        {/* Chart Container */}
        <AppBoxComponent
          sx={{
            height: { xs: 250, md: 400 },
            backgroundColor: "transparent",
            borderRadius: theme.spacing(1),
            mb: { xs: theme.spacing(2), md: theme.spacing(3) },
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isLoading ? (
            <LinearProgress sx={{ width: "50%" }} />
          ) : chartData && chartData.length > 0 ? (
            <CommonAreaChart
              id="navHistoryChart"
              chartData={chartData}
              tooltipText={`NAV: {valueY.formatNumber('#.00')}
{valueX.formatDate('dd-MMM-yyyy')}`}
            />
          ) : (
            <AppTypographyComponent
              sx={{ color: theme.palette.text.secondary }}
            >
              No Data Found
            </AppTypographyComponent>
          )}
        </AppBoxComponent>

        {/* Time Range Buttons */}
        <AppBoxComponent
          sx={{
            display: "flex",
            gap: { xs: theme.spacing(0.8), md: theme.spacing(3) },
            justifyContent: "center",
          }}
        >
          {TIME_RANGES.map((range) => {
            const isSelected = selectedTimeRange === range;
            return (
              <AppButtonComponent
                key={range}
                onClick={() => handleTimeRangeChange(range)}
                sx={{
                  px: { xs: theme.spacing(1.8), md: theme.spacing(2.5) },
                  py: { xs: theme.spacing(0.1), md: theme.spacing(0.75) },
                  borderRadius: {
                    md: theme.spacing(2.75),
                    xs: theme.spacing(2.5),
                  },
                  textTransform: "none",
                  fontSize: {
                    xs: typography.fontSize.helperText,
                    md: typography.fontSize.label,
                  },
                  fontWeight: isSelected
                    ? typography.fontWeight.semibold
                    : typography.fontWeight.regular,
                  backgroundColor: isSelected
                    ? theme.palette.custom?.button?.primary
                    : "transparent",
                  color: theme.palette.custom?.text?.heading,
                  border: isSelected
                    ? "none"
                    : `1px solid ${theme.palette.custom?.stroke?.outlineSecondary}`,
                  minWidth: "auto",
                  "&:hover": {
                    backgroundColor: isSelected
                      ? theme.palette.custom?.button?.primary
                      : "transparent",
                    opacity: isSelected ? 0.9 : 0.8,
                  },
                }}
              >
                {range}
              </AppButtonComponent>
            );
          })}
        </AppBoxComponent>
      </AppBoxComponent>

      {performanceComingSoon && (
        <AppBoxComponent
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <ComingSoonCard />
        </AppBoxComponent>
      )}
    </AppCardComponent>
  );

  const allocationSection = (
    <AppCardComponent
      sx={{
        p: theme.spacing(1),
        mb: theme.spacing(3),
        backgroundColor:
          theme.palette.custom?.background?.card || theme.palette.grey[900],
        border: "none",
        borderRadius: theme.spacing(1.5),
      }}
    >
      <AppTypographyComponent
        sx={{
          color: theme.palette.custom?.text?.heading,
          fontSize: {
            xs: typography.fontSize.body1,
            md: typography.fontSize.h4,
          },
          fontWeight: typography.fontWeight.semibold,
          mb: { xs: theme.spacing(2), md: theme.spacing(3) },
        }}
      >
        Allocation
      </AppTypographyComponent>

      {/* Tab Buttons (Filter Options) */}
      <AppBoxComponent
        sx={{
          display: "flex",
          gap: 2,
          mb: 4,
          flexWrap: "wrap",
        }}
      >
        {FILTER_OPTIONS.map((filter) => (
          <AppButtonComponent
            key={filter}
            onClick={() => handleFilterChange(filter)}
            sx={{
              flex: 1,
              py: 1,
              borderRadius: theme.spacing(1),
              textTransform: "none",
              fontSize: typography.fontSize.label,
              fontWeight: typography.fontWeight.medium,
              backgroundColor:
                selectedFilter === filter ? alpha("#3B82F6", 0.1) : "transparent",
              color: selectedFilter === filter ? "#3B82F6" : "#9CA3AF",
              border: `1px solid ${selectedFilter === filter ? "#3B82F6" : "#374151"
                }`,
              minWidth: "120px",
              "&:hover": {
                backgroundColor:
                  selectedFilter === filter
                    ? alpha("#3B82F6", 0.2)
                    : alpha("#374151", 0.1),
                borderColor: selectedFilter === filter ? "#3B82F6" : "#4B5563",
              },
            }}
          >
            {filter}
          </AppButtonComponent>
        ))}
      </AppBoxComponent>

      <Grid container spacing={4} alignItems="center">
        {/* Left Side - Asset List */}
        <Grid item xs={12} md={6}>
          <AppBoxComponent>
            {mappedAssetCategories.map((category, index) => (
              <AppBoxComponent key={index} sx={{ mb: 3 }}>
                <AppBoxComponent
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <AppTypographyComponent
                    sx={{ color: "white", fontSize: "14px", fontWeight: 500 }}
                  >
                    {category.name}
                  </AppTypographyComponent>
                  <AppTypographyComponent
                    sx={{ color: "white", fontSize: "14px", fontWeight: 600 }}
                  >
                    {category.percentage.toFixed(2)}%
                  </AppTypographyComponent>
                </AppBoxComponent>
                <AppBoxComponent
                  sx={{
                    height: 6,
                    width: "100%",
                    backgroundColor: "#374151",
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <AppBoxComponent
                    sx={{
                      height: "100%",
                      width: `${category.percentage}%`,
                      background: category.gradient,
                      borderRadius: 3,
                    }}
                  />
                </AppBoxComponent>
              </AppBoxComponent>
            ))}
          </AppBoxComponent>
        </Grid>

        {/* Right Side - Donut Chart */}
        <Grid item xs={12} md={6}>
          <DonutChartContainer
            assetAllocationData={mappedAssetCategories.map((cat) => ({
              ...cat,
              color: cat.solid || cat.gradient, // Pass solid color, fallback to gradient if needed (though donut expects solid usually)
            }))}
            title="Asset Allocation"
            height={300}
          />
        </Grid>
      </Grid>
    </AppCardComponent>
  );

  const holdingsSection = (
    <AppCardComponent
      sx={{
        p: theme.spacing(1),
        mb: theme.spacing(3),
        backgroundColor:
          theme.palette.custom?.background?.card || theme.palette.grey[900],
        border: "none",
        borderRadius: theme.spacing(1.5),
      }}
    >
      <AppTypographyComponent
        sx={{
          color: theme.palette.custom?.text?.heading,
          fontSize: {
            xs: typography.fontSize.body1,
            md: typography.fontSize.h4,
          },
          fontWeight: typography.fontWeight.semibold,
          mb: { xs: theme.spacing(2), md: theme.spacing(3) },
        }}
      >
        Your holdings
      </AppTypographyComponent>

      {/* Holdings Row — Two separate cards */}
      <AppBoxComponent
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: theme.spacing(2),
        }}
      >
        {/* Mutual Funds Card */}
        <AppBoxComponent
          onClick={() => navigate(ROUTES.PORTFOLIO_MUTUAL_FUND)}
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            p: theme.spacing(2),
            borderRadius: theme.spacing(1),
            border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
            backgroundColor: alpha(theme.palette.common.white, 0.03),
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: alpha(theme.palette.common.white, 0.06),
              borderColor: alpha(theme.palette.common.white, 0.18),
            },
          }}
        >
          <AppBoxComponent>
            <AppTypographyComponent
              sx={{
                color: theme.palette.custom?.text?.heading || theme.palette.common.white,
                fontSize: typography.fontSize.h4,
                fontWeight: typography.fontWeight.bold,
              }}
            >
              Mutual Funds
            </AppTypographyComponent>
            <AppTypographyComponent
              sx={{
                color: theme.palette.custom?.text?.helperDisabled || theme.palette.text.disabled,
                fontSize: typography.fontSize.paragraph,
              }}
            >
              {mutualFundsData.countValue || "—"}
            </AppTypographyComponent>
          </AppBoxComponent>

          <AppBoxComponent sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AppBoxComponent sx={{ textAlign: "right" }}>
              <AppTypographyComponent
                sx={{
                  color: theme.palette.custom?.text?.heading || theme.palette.common.white,
                  fontSize: typography.fontSize.h4,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                {formatToLacOrCr(mutualFundsData.currentValue)}
              </AppTypographyComponent>
              {mutualFundsData.returns !== null && mutualFundsData.returns !== undefined && (
                <AppTypographyComponent
                  sx={{
                    color: mutualFundsData.returns >= 0 ? theme.palette.success.main : theme.palette.error.main,
                    fontSize: typography.fontSize.label,
                    fontWeight: typography.fontWeight.semibold,
                  }}
                >
                  {renderReturns(mutualFundsData.returns, mutualFundsData.oneDayReturns)}
                </AppTypographyComponent>
              )}
            </AppBoxComponent>
            <ChevronRightIcon sx={{ color: alpha(theme.palette.primary.main, 0.6), fontSize: 20 }} />
          </AppBoxComponent>
        </AppBoxComponent>

        {/* Bonds Card */}
        <AppBoxComponent
          onClick={() => navigate(ROUTES.PORTFOLIO_BOND)}
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            p: theme.spacing(2),
            borderRadius: theme.spacing(1),
            border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
            backgroundColor: alpha(theme.palette.common.white, 0.03),
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: alpha(theme.palette.common.white, 0.06),
              borderColor: alpha(theme.palette.common.white, 0.18),
            },
          }}
        >
          <AppBoxComponent>
            <AppTypographyComponent
              sx={{
                color: theme.palette.custom?.text?.heading || theme.palette.common.white,
                fontSize: typography.fontSize.h4,
                fontWeight: typography.fontWeight.bold,
              }}
            >
              Bonds
            </AppTypographyComponent>
            <AppTypographyComponent
              sx={{
                color: theme.palette.custom?.text?.helperDisabled || theme.palette.text.disabled,
                fontSize: typography.fontSize.paragraph,
              }}
            >
              {bondsData.countValue || "—"}
            </AppTypographyComponent>
          </AppBoxComponent>

          <AppBoxComponent sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AppBoxComponent sx={{ textAlign: "right" }}>
              <AppTypographyComponent
                sx={{
                  color: theme.palette.custom?.text?.heading || theme.palette.common.white,
                  fontSize: typography.fontSize.h4,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                {formatToLacOrCr(bondsData.currentValue)}
              </AppTypographyComponent>
              {bondsData.returns !== null && bondsData.returns !== undefined && (
                <AppTypographyComponent
                  sx={{
                    color: bondsData.returns >= 0 ? theme.palette.success.main : theme.palette.error.main,
                    fontSize: typography.fontSize.label,
                    fontWeight: typography.fontWeight.semibold,
                  }}
                >
                  {renderReturns(bondsData.returns, bondsData.oneDayReturns)}
                </AppTypographyComponent>
              )}
            </AppBoxComponent>
            <ChevronRightIcon sx={{ color: alpha(theme.palette.primary.main, 0.6), fontSize: 20 }} />
          </AppBoxComponent>
        </AppBoxComponent>
      </AppBoxComponent>
    </AppCardComponent>
  );

  return (
    <AppBoxComponent
      sx={{
        backgroundColor: "transparent",
        minHeight: "auto",
      }}
    >
      {reverseOrder ? (
        <>
          {allocationSection}
          {holdingsSection}
          {performanceSection}
        </>
      ) : (
        <>
          {performanceSection}
          {holdingsSection}
          {allocationSection}
        </>
      )}
    </AppBoxComponent>
  );
};

PortfolioSummaryView.propTypes = {
  portfolioData: PropTypes.object,
  performanceComingSoon: PropTypes.bool,
  reverseOrder: PropTypes.bool,
};

PortfolioSummaryView.defaultProps = {
  portfolioData: {},
  performanceComingSoon: false,
  reverseOrder: false,
  holdingsCardsResponse: null,
};

export default PortfolioSummaryView
